/**
 * Mobile sticky add-to-cart controller.
 *
 * The sticky bar is a thin controller over the main product-form: selecting a
 * size drives the real size radio (the single source of truth) and submitting
 * goes through the main product-form, so the capture-phase variant-id-sync guard
 * still resolves the correct variant. Sold-out sizes route to Notify Me / Restock
 * Rocket. The bar shows only on mobile when the main Add to Cart button is off
 * screen and the cart drawer is closed.
 */
(() => {
  const bar = document.getElementById('mobile-sticky-atc');
  if (!bar) return;

  const panel = bar.querySelector('[data-sticky-atc="panel"]');
  const cta = bar.querySelector('[data-sticky-atc="cta"]');
  const ctaLabel = bar.querySelector('[data-sticky-atc="cta-label"]');
  const closeBtn = bar.querySelector('[data-sticky-atc="close"]');
  const options = [...bar.querySelectorAll('[data-sticky-atc="option"]')];
  const optionName = bar.getAttribute('data-option-name');
  const addToCartLabel = ctaLabel ? ctaLabel.textContent.trim() : '';
  const mqMobile = window.matchMedia('(max-width: 1023px)');

  let mainButtonVisible = true;
  let cartDrawerOpen = false;
  let submitting = false;
  let buttonObserver = null;
  let visibilitySyncTimer = null;

  // The theme replaces the product-form on variant change, so always query live.
  const getMainDetails = () =>
    [...document.querySelectorAll('variant-details[data-section-id]')].find(
      (el) => !el.closest('modal-dialog') && !el.closest('quick-add-to-cart')
    ) || null;

  const getMainForm = () => {
    const details = getMainDetails();
    return details
      ? details.querySelector('product-form[data-target="variant-details.product-form"] form')
      : null;
  };

  const getMainSubmit = () => {
    const details = getMainDetails();
    return details
      ? details.querySelector('product-form[data-target="variant-details.product-form"] button[type="submit"]')
      : null;
  };

  const getSizeRadios = () => {
    const details = getMainDetails();
    if (!details || !optionName) return [];
    return [
      ...details.querySelectorAll(
        '.pdp-size-grid input[type="radio"][name="' + optionName + '"]'
      ),
    ];
  };

  const getVariants = () => {
    const details = getMainDetails();
    const variantsJson = details
      ? details.querySelector('[data-target="variant-select.variants-json"]')
      : null;
    if (!variantsJson) return [];
    try {
      const variants = JSON.parse(variantsJson.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  };

  const getSelectedOptions = () => {
    const details = getMainDetails();
    if (!details) return [];
    const selected = [];
    details.querySelectorAll('input[type="radio"][name^="option"]:checked').forEach((input) => {
      const match = input.name.match(/^option(\d+)$/);
      if (match) selected[Number(match[1]) - 1] = input.value;
    });
    return selected;
  };

  const getStickyOption = (value) =>
    options.find((option) => option.dataset.value === value) || null;

  // Availability lives on the sticky options, not on main radios: the theme
  // rebuilds those radios after variant changes and drops their data attributes.
  const syncOptionAvailability = () => {
    const sizeMatch = optionName ? optionName.match(/^option(\d+)$/) : null;
    const variants = getVariants();
    if (!sizeMatch || variants.length === 0) return;
    const sizeIndex = Number(sizeMatch[1]) - 1;
    const selectedOptions = getSelectedOptions();
    const radios = getSizeRadios();

    options.forEach((option) => {
      const expectedOptions = [...selectedOptions];
      expectedOptions[sizeIndex] = option.dataset.value;
      const variant = variants.find(
        (item) =>
          Array.isArray(item.options) &&
          item.options.every(
            (value, index) => expectedOptions[index] == null || expectedOptions[index] === value
          )
      );
      const soldOut = !!variant && variant.available === false;
      const radio = radios.find((item) => item.value === option.dataset.value);
      let lowStock = option.getAttribute('data-low-stock') === 'true';
      if (radio && radio.hasAttribute('data-low-stock')) {
        lowStock = radio.getAttribute('data-low-stock') === 'true';
      } else if (
        variant &&
        variant.inventory_quantity != null &&
        variant.inventory_policy != null
      ) {
        lowStock =
          variant.available === true &&
          variant.inventory_policy !== 'continue' &&
          Number(variant.inventory_quantity) > 0 &&
          Number(variant.inventory_quantity) < 3;
      }
      if (!variant || soldOut) lowStock = false;

      option.disabled = !variant;
      if (soldOut) option.setAttribute('data-sold-out', 'true');
      else option.removeAttribute('data-sold-out');
      option.setAttribute('data-low-stock', String(lowStock));
    });
  };

  const getCheckedRadio = () => getSizeRadios().find((radio) => radio.checked) || null;
  const isVisible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };
  // Prefer the current product's hidden theme label button: Restock Rocket's
  // visible cover often has empty textContent, which must not wipe the CTA label.
  const getNotifyLabelButton = () => {
    const details = getMainDetails();
    return details ? details.querySelector('button[id^="restock-"]') : null;
  };
  // Click target for Notify Me. Prefer the main buy box; if Restock Rocket injects
  // just outside variant-details, allow the surrounding product section only.
  // Never fall back to document-wide matches from other product cards.
  const getNotifyButton = () => {
    const details = getMainDetails();
    if (!details) return null;
    const selectors = [
      '.restock-rocket-button-cover',
      '.restock-rocket-button-product',
      'button[id^="restock-"]',
    ];
    const scopes = [details, details.closest('.shopify-section')].filter(Boolean);

    for (const scope of scopes) {
      for (const selector of selectors) {
        const notify = scope.querySelector(selector);
        if (
          isVisible(notify) &&
          !notify.closest('modal-dialog') &&
          !notify.closest('quick-add-to-cart')
        ) {
          return notify;
        }
      }
    }

    return null;
  };
  const isSoldOut = (radio) => {
    const option = radio ? getStickyOption(radio.value) : null;
    return !!option && option.getAttribute('data-sold-out') === 'true';
  };
  const isLowStock = (radio) => {
    const option = radio ? getStickyOption(radio.value) : null;
    return (
      !!option &&
      option.getAttribute('data-low-stock') === 'true' &&
      option.getAttribute('data-sold-out') !== 'true'
    );
  };

  const updateCta = (checked) => {
    if (!ctaLabel) return;
    if (isSoldOut(checked)) {
      const notifyButton = getNotifyButton();
      const notifyLabelButton = getNotifyLabelButton();
      // Prefer a visible notify control when it has real copy; otherwise use the
      // theme label button (often hidden, but holds the translated "Notify me").
      const notifyLabel =
        (notifyButton && notifyButton.textContent.trim()) ||
        (notifyLabelButton && notifyLabelButton.textContent.trim()) ||
        addToCartLabel;
      ctaLabel.textContent = notifyLabel;
      cta.classList.add('mobile-sticky-atc__cta--notify');
    } else {
      ctaLabel.textContent = addToCartLabel;
      cta.classList.remove('mobile-sticky-atc__cta--notify');
    }
    cta.setAttribute('aria-label', ctaLabel.textContent);
  };

  // Mirror the main selection into the panel buttons, CTA and low-stock message.
  const reflectFromMain = () => {
    syncOptionAvailability();
    const checked = getCheckedRadio();
    options.forEach((option) => {
      option.setAttribute(
        'aria-pressed',
        checked && option.dataset.value === checked.value ? 'true' : 'false'
      );
    });
    updateCta(checked);
    bar.classList.toggle('mobile-sticky-atc--low-stock', isLowStock(checked));
  };

  const openPanel = () => {
    if (!panel) return;
    reflectFromMain();
    panel.hidden = false;
    bar.classList.add('mobile-sticky-atc--expanded');
  };

  const closePanel = () => {
    if (!panel) return;
    panel.hidden = true;
    bar.classList.remove('mobile-sticky-atc--expanded');
  };

  const show = () => {
    bar.classList.add('mobile-sticky-atc--visible');
    bar.removeAttribute('aria-hidden');
  };

  const hide = () => {
    bar.classList.remove('mobile-sticky-atc--visible');
    bar.setAttribute('aria-hidden', 'true');
  };

  const updateVisibility = () => {
    if (mqMobile.matches && !mainButtonVisible && !cartDrawerOpen) {
      show();
    } else {
      hide();
      closePanel();
    }
  };

  // Resize fires far more often than the other triggers (IntersectionObserver,
  // media-query change, cart-drawer toggle), so only the resize listener is
  // debounced; those other callers still update visibility immediately.
  const scheduleUpdateVisibility = () => {
    window.clearTimeout(visibilitySyncTimer);
    visibilitySyncTimer = window.setTimeout(updateVisibility, 80);
  };

  // Drive the real size radio so the theme pipeline + variant-id-sync stay in sync.
  const selectSize = (value) => {
    const radio = getSizeRadios().find((item) => item.value === value);
    if (!radio || radio.disabled) return null;
    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    reflectFromMain();
    return radio;
  };

  const triggerNotify = () => {
    closePanel();
    hide();
    let attempts = 0;
    const clickNotify = () => {
      const notify = getNotifyButton();
      if (notify) {
        notify.click();
        return;
      }
      attempts += 1;
      if (attempts < 6) window.setTimeout(clickNotify, 100);
    };
    clickNotify();
  };

  const submitMain = () => {
    if (submitting) return;
    const form = getMainForm();
    const submit = getMainSubmit();
    if (!form || !submit || submit.disabled) return;
    submitting = true;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(submit);
    else submit.click();
    window.setTimeout(() => {
      submitting = false;
    }, 1500);
  };

  options.forEach((option) => {
    option.addEventListener('click', () => {
      if (option.disabled) return;
      const radio = selectSize(option.dataset.value);
      if (!radio) return;
      if (isSoldOut(radio)) {
        triggerNotify();
        return;
      }
      closePanel();
      submitMain();
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', closePanel);

  cta.addEventListener('click', () => {
    if (submitting) return;
    if (bar.classList.contains('mobile-sticky-atc--expanded')) {
      const checked = getCheckedRadio();
      if (!checked) return;
      if (isSoldOut(checked)) {
        triggerNotify();
        return;
      }
      closePanel();
      submitMain();
      return;
    }
    // First tap always opens the size grid so the customer can review or change
    // a size that may have been pre-selected on the main PDP before adding to cart.
    openPanel();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && bar.classList.contains('mobile-sticky-atc--expanded')) {
      closePanel();
    }
  });

  // Reflect selections made directly on the main size grid.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (
      target &&
      target.matches &&
      target.matches('input[type="radio"][name="' + optionName + '"]') &&
      !target.closest('#mobile-sticky-atc')
    ) {
      reflectFromMain();
    }
  });

  const observeMainButton = () => {
    // Only the current product's submit button controls sticky visibility.
    // Notify buttons are intentionally excluded: getNotifyButton can still match
    // Restock Rocket nodes near the buy box, and a document-wide false positive
    // would hide the sticky bar while the main ATC is off-screen.
    const submit = getMainSubmit();
    if (!submit || !('IntersectionObserver' in window)) {
      mainButtonVisible = false;
      updateVisibility();
      return;
    }
    if (buttonObserver) buttonObserver.disconnect();
    buttonObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          mainButtonVisible = entry.isIntersecting && isVisible(entry.target);
        });
        updateVisibility();
      },
      { threshold: 0 }
    );
    buttonObserver.observe(submit);
  };

  const setupCartDrawerDetection = () => {
    if (typeof subscribe === 'function') {
      subscribe('modalToggle', (event) => {
        if (event && event.id === 'cart-drawer') {
          cartDrawerOpen = !!event.open;
          if (cartDrawerOpen) submitting = false;
          updateVisibility();
        }
      });
      subscribe('variantUpdate', () => {
        observeMainButton();
        reflectFromMain();
      });
      return;
    }
    // Fallback when the theme pub/sub is unavailable.
    const drawer = document.querySelector('modal-dialog[data-id="cart-drawer"]');
    if (!drawer || !('MutationObserver' in window)) return;
    new MutationObserver(() => {
      cartDrawerOpen = drawer.hasAttribute('data-visible');
      if (cartDrawerOpen) submitting = false;
      updateVisibility();
    }).observe(drawer, { attributes: true, attributeFilter: ['data-visible'] });
  };

  const init = () => {
    observeMainButton();
    setupCartDrawerDetection();
    reflectFromMain();
    if (typeof mqMobile.addEventListener === 'function') {
      mqMobile.addEventListener('change', updateVisibility);
    }
    window.addEventListener('resize', scheduleUpdateVisibility, { passive: true });
    updateVisibility();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
